import React, { useContext, useEffect, useRef, useState } from 'react'
import RemindersList from '@interflora/ui-components/build/components/RemindersListing/RemindersListing'
import { SiteContext, formatAddEditOccasionValues } from '@interflora/ui-components/build/utils/common'
import {
  DELETE_OCCASION_REMINDER,
  GET_CUSTOMER_AVAILABLE_REMINDER_MONTHS,
  GET_MY_ACCOUNT_REMINDERS_LIST,
  SAVE_OCCASION_REMINDERS,
  EDIT_OCCASION_REMINDER,
} from '../../graphql/queries'
import { AddEditOccasionValues, HandleSubmit, ReminderProps } from '@interflora/ui-components/build/common/props'
import { useMutation, useQuery } from '@apollo/client'
import _get from 'lodash-es/get'
import AnalyticsContext from 'context/AnalyticsContext'

const PER_PAGE = 9

const Reminders = () => {
  const { getCustomer } = useContext(SiteContext)
  const Analytics = useContext(AnalyticsContext)
  const [page, setPage] = useState(1)
  const [totalReminders, setTotalReminders] = useState<number>()
  const [availableMonths, setAvailableMonths] = useState<number[]>()
  const [reminders, setReminders] = useState<ReminderProps[]>([])
  const [isAdvancedListing, setAdvancedListing] = useState(false)
  const [isLastPage, setLastPage] = useState(false)
  const [monthCount, setMonthCount] = useState<number>()
  const [month, setMonth] = useState<number>()
  const [isLoadAll, setLoadAll] = useState(false)
  const currentMonth = useRef<number>()
  const currentPage = useRef<number>()

  const [addNewReminderMutation] = useMutation(SAVE_OCCASION_REMINDERS)
  const [editReminderMutation] = useMutation(EDIT_OCCASION_REMINDER)
  const [deleteCustomerOccasionReminder] = useMutation(DELETE_OCCASION_REMINDER)

  const { refetch, loading } = useQuery(GET_CUSTOMER_AVAILABLE_REMINDER_MONTHS, {
    fetchPolicy: 'network-only',
    ssr: false,
  })
  const { refetch: reminderListRefetch } = useQuery(GET_MY_ACCOUNT_REMINDERS_LIST, {
    skip: loading || !totalReminders || !availableMonths?.length,
    variables: {
      input: {
        page,
        pageSize: isAdvancedListing ? PER_PAGE : 50,
        month: isAdvancedListing ? month : undefined,
        isLoadAll,
      },
    },
  })

  const setAvailableReminderProperties = (response) => {
    if (!!response?.data?.getOccasionReminderAvailableMonths) {
      const { totalCount, availableMonths, currentActiveMonth } = response.data.getOccasionReminderAvailableMonths
      setTotalReminders(totalCount)
      setAvailableMonths(availableMonths)
      setAdvancedListing(totalCount > 9)
      setMonth(currentActiveMonth)
      currentMonth.current = currentActiveMonth
    }
  }

  const setRemindersProperties = (response, month: number, page: number, isModifiyingReminder: boolean) => {
    let previousArray = reminders
    if (!!response?.data?.getMyOccasionReminders) {
      if (isAdvancedListing && isModifiyingReminder && !!previousArray?.length && !isLoadAll) {
        page === 1 || month !== currentMonth.current
          ? (previousArray = [])
          : previousArray.splice(page * PER_PAGE - PER_PAGE, previousArray.length)
      }
      const { isLastPage, count, occasionReminders } = response.data.getMyOccasionReminders
      const concatPreviousReminders =
        isAdvancedListing &&
        page !== 1 &&
        currentMonth.current === month &&
        (isModifiyingReminder || currentPage.current !== page) &&
        !isLoadAll
      if (concatPreviousReminders) {
        setReminders(previousArray.concat(occasionReminders))
      } else {
        setReminders(occasionReminders)
      }
      setLastPage(isLastPage)
      setMonthCount(count)
      currentMonth.current = month
      currentPage.current = page
    }
  }

  useEffect(() => {
    ;(async () => {
      const response = await refetch()
      setAvailableReminderProperties(response)
    })()
  }, [refetch])

  useEffect(() => {
    if (month !== currentMonth.current) {
      setLoadAll(false)
    }
    ;(async () => {
      if (totalReminders) {
        const data = await reminderListRefetch()
        setRemindersProperties(data, month, page, false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderListRefetch, month, page])

  const addNewReminder: HandleSubmit<AddEditOccasionValues> = async (values) => {
    try {
      const response = await addNewReminderMutation({
        variables: { input: formatAddEditOccasionValues(values) },
      })
      Analytics.occasions([values.occasion], 'My Account')
      /** refetching reminders availableMonths and total count */
      refetch().then(({ data }) => {
        setTotalReminders(data?.getOccasionReminderAvailableMonths?.totalCount)
        if (isAdvancedListing) setAvailableMonths(data?.getOccasionReminderAvailableMonths?.availableMonths)

        /** refetching reminders list */
        reminderListRefetch().then((data) => {
          setRemindersProperties(data, month, currentPage.current, true)
        })
      })

      return response
    } catch (error) {
      const errMsg = _get(error, ['graphQLErrors', 0, 'message'])
      throw new Error(!!errMsg ? errMsg : 'There was a problem. Please try again')
    }
  }

  const editReminder: HandleSubmit<AddEditOccasionValues> = async (values) => {
    try {
      const response = await editReminderMutation({
        variables: { input: formatAddEditOccasionValues(values, true) },
      })
      /** refetching reminders list */
      const reminderResponse = await reminderListRefetch()
      const monthNumber = reminderResponse?.data?.getMyOccasionReminders.count
      /** refetching reminders availableMonths and total count */
      const availabelMonthResponse = await refetch()
      const totalCount = availabelMonthResponse?.data?.getOccasionReminderAvailableMonths?.totalCount
      if (monthNumber === 0) {
        setMonth(availabelMonthResponse?.data?.getOccasionReminderAvailableMonths?.currentActiveMonth)
      }
      if (totalCount === 0) {
        setReminders([])
      } else {
        if (monthNumber > 0) setRemindersProperties(reminderResponse, month, currentPage.current, true)
      }
      setTotalReminders(totalCount)
      setAvailableMonths(availabelMonthResponse?.data?.getOccasionReminderAvailableMonths?.availableMonths)

      return response
    } catch (error) {
      const errMsg = _get(error, ['graphQLErrors', 0, 'message'])
      throw new Error(!!errMsg ? errMsg : 'There was a problem. Please try again')
    }
  }

  const deleteReminder = async (reminderId: string) => {
    try {
      await deleteCustomerOccasionReminder({ variables: { input: reminderId } })
      /** refetching reminders list */
      const reminderResponse = await reminderListRefetch()
      const monthNumber = reminderResponse?.data?.getMyOccasionReminders.count
      /** availabele monthe refetch */
      const availabelMonthResponse = await refetch()
      const totalCount = availabelMonthResponse?.data?.getOccasionReminderAvailableMonths?.totalCount
      if (monthNumber === 0) {
        setMonth(availabelMonthResponse?.data?.getOccasionReminderAvailableMonths?.currentActiveMonth)
      }
      if (monthNumber > 0 && totalCount > 0) setRemindersProperties(reminderResponse, month, currentPage.current, true)
      if (totalCount === 0) setReminders([])
      setTotalReminders(totalCount)
      setAvailableMonths(availabelMonthResponse?.data?.getOccasionReminderAvailableMonths?.availableMonths)
    } catch (error) {
      throw new Error('There was a problem , please try again.')
    }
  }

  return getCustomer.loading || !getCustomer.data?.customer?.id ? (
    <h1>Loading</h1>
  ) : (
    <RemindersList
      addNewReminder={addNewReminder}
      editReminder={editReminder}
      deleteReminder={deleteReminder}
      availableMonths={availableMonths}
      reminders={reminders}
      totalReminders={totalReminders}
      setPage={setPage}
      page={page}
      isAdvancedListing={isAdvancedListing}
      currentActiveMonth={month}
      setMonth={setMonth}
      isLastPage={isLastPage}
      monthCount={monthCount}
      setLoadAll={setLoadAll}
    />
  )
}
export default Reminders
